import React, { useState } from 'react';
import { MapPin, ShieldCheck, AlertTriangle, CheckCircle, Upload, Satellite, Calendar } from 'lucide-react';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';

const PropertyInfo = ({ 
  estimate, 
  onImportRoofr, 
  onImportEagleview, 
  onOverrideMeasurement 
}) => {
  const [imgError, setImgError] = useState(false);
  
  // Use the better of measurement_confidence.score or accuracy_rating
  const mcScore = estimate.measurement_confidence?.score || 0;
  const arScore = estimate.accuracy_rating ? Math.round(estimate.accuracy_rating) : 0;
  const confidenceScore = Math.max(mcScore, arScore);
  const confidenceLevel = estimate.measurement_confidence?.level || 
    (confidenceScore >= 80 ? 'high' : confidenceScore >= 60 ? 'medium' : 'low');
  
  const measurementMethod = estimate.measurement_confidence?.comparison_data?.method || '';
  const isFallback = measurementMethod === 'solar_corrected' || measurementMethod === 'no_data_available';
  const isCached = measurementMethod === 'cached_footprint_with_pitch';
  const warnings = estimate.measurement_confidence?.warnings || [];
  
  const getConfidenceColor = () => {
    if (isFallback) return 'text-orange-700 bg-orange-50 border-orange-300';
    if (confidenceScore >= 85) return 'text-green-600 bg-green-50 border-green-200';
    if (confidenceScore >= 70) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getConfidenceIcon = () => {
    if (confidenceScore >= 85 && !isFallback) return <ShieldCheck className="w-5 h-5" />;
    return <AlertTriangle className="w-5 h-5" />;
  };
  
  const getConfidenceLabel = () => {
    if (isCached) return 'Verified (Cached)';
    if (isFallback) return 'Estimated (Satellite Fallback)';
    if (confidenceScore >= 85) return 'High';
    if (confidenceScore >= 70) return 'Medium';
    return 'Low';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
        <MapPin className="w-5 h-5 mr-2 text-blue-600" />
        Property Information
      </h2>
      
      {/* Address */}
      <div className="mb-4">
        <Label className="text-gray-600">Address</Label>
        <p className="text-lg text-gray-900 font-medium">{estimate.address}</p>
      </div>

      {/* Satellite View */}
      {estimate.static_map_url && !imgError && (
        <div className="mb-4" data-testid="satellite-imagery-section">
          <div className="rounded-lg overflow-hidden border border-gray-200">
            <img 
              src={estimate.static_map_url} 
              alt="Property satellite view" 
              className="w-full h-auto"
              onError={() => setImgError(true)}
            />
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Satellite className="w-3 h-3" />
              Satellite View
            </span>
            {estimate.imagery_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Imagery: {estimate.imagery_date}
              </span>
            )}
            {estimate.measurement_confidence?.comparison_data?.footprint_source && (
              <span className="text-blue-600">
                Source: {estimate.measurement_confidence.comparison_data.footprint_source}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Property Details Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-gray-600">Square Footage</Label>
          <p className="text-gray-900 font-semibold text-xl">
            {estimate.satellite_data?.roof_squares 
              ? `${(estimate.satellite_data.roof_squares * 100).toLocaleString()} sq ft`
              : estimate.square_footage 
                ? `${estimate.square_footage.toLocaleString()} sq ft`
                : 'N/A'
            }
          </p>
        </div>
        <div>
          <Label className="text-gray-600">Roof Type</Label>
          <p className="text-gray-900 font-medium capitalize">
            {estimate.roof_type || 'Asphalt Shingle'}
          </p>
        </div>
      </div>

      {/* Measurement Confidence */}
      <div className={`p-4 rounded-lg border ${getConfidenceColor()} mb-4`} data-testid="measurement-confidence">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getConfidenceIcon()}
            <span className="font-semibold">
              Measurement Confidence: {confidenceScore}/100 ({getConfidenceLabel()})
            </span>
          </div>
          {estimate.measurement_verified && (
            <span className="flex items-center gap-1 text-green-700 text-sm">
              <CheckCircle className="w-4 h-4" />
              Verified
            </span>
          )}
        </div>
        
        {/* Show data source */}
        <p className="text-sm mt-1 opacity-80">
          Source: {estimate.measurement_confidence?.comparison_data?.footprint_source || 'satellite'}
          {isCached && ' (cached for consistency)'}
        </p>
        
        {/* Show warnings prominently for fallback methods */}
        {isFallback && (
          <div className="mt-2 p-2 bg-orange-100 rounded text-sm font-medium">
            This measurement used a satellite-estimated fallback because building footprint data was unavailable. 
            Consider using the Override button or importing an Eagleview/Roofr report for a more accurate result.
          </div>
        )}
        
        {/* Show any warnings from the measurement engine */}
        {warnings.length > 0 && !isFallback && (
          <div className="mt-2 space-y-1">
            {warnings.map((w, i) => (
              <p key={i} className="text-xs opacity-80">{w}</p>
            ))}
          </div>
        )}
        
        <p className="text-sm mt-2">
          {estimate.measurement_confidence?.recommendations?.[0] || 
           'Measurement based on verified building footprint data'}
        </p>
      </div>

      {/* Measurement Disclaimer */}
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4" data-testid="estimate-disclaimer">
        <p className="text-xs text-amber-800 leading-relaxed">
          <span className="font-semibold">Disclaimer:</span> This estimate is generated using satellite imagery, AI-powered building footprint data, and industry-standard pricing models. While our measurements are highly accurate, a physical on-site inspection is required to confirm all conditions, including hidden damage, structural factors, or site-specific variables not identifiable through remote estimation. Final pricing may vary based on inspection findings.
        </p>
      </div>

      {/* Import Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onImportRoofr}
          className="border-green-400 text-green-700 hover:bg-green-50"
        >
          <Upload className="w-4 h-4 mr-1" />
          Import Roofr
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onImportEagleview}
          className="border-blue-400 text-blue-700 hover:bg-blue-50"
        >
          <Upload className="w-4 h-4 mr-1" />
          Import Eagleview
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onOverrideMeasurement}
          className="border-gray-400 text-gray-700 hover:bg-gray-50"
        >
          Override
        </Button>
      </div>
    </div>
  );
};

export default PropertyInfo;
